import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';

import {
  DataTable,
  DataTableColumn,
  DataTableCell,
  Icon,
  Input,
  InputIcon,
  Tooltip
} from '@salesforce/design-system-react';

import './ContentBuilderList.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const { formatMessage } = props.intl;
  switch (property) {
    case 'disabled_reason':
      return (
        <td className="disabled-cell">
          {props.item?.disabled ? (
            <Tooltip
              id="disabled_reason"
              align="bottom left"
              content={formatMessage({
                id: `components.ecs.contentBuilderModal.${props.item?.disabled_reason}`
              })}
              variant="learnMore"
              dialogClassName="disable-tooltip"
            />
          ) : null}
        </td>
      );
    case 'name':
      return <td className="cb-cell truncate">{children}</td>;
    case 'thumbnail':
      if (props.item?.disabled_reason === 'image unreachable') {
        return (
          <td>
            <div className="thumbnail-container unreachable">
              <Icon category="utility" colorVariant="default" name="warning" />
            </div>
          </td>
        );
      }
      const url = props.item?.fileProperties?.publishedURL;
      if (url) {
        return (
          <td>
            <div
              className="thumbnail-container"
              style={{ backgroundImage: `url(${url})` }}
            />
          </td>
        );
      } else if (props.item?.content) {
        return (
          <td>
            <div className="thumbnail-container">
              <iframe
                title={`Template thumbnail ${props.item?.id}`}
                className="thumbnail-iframe"
                scrolling="no"
                frameBorder="0"
                srcDoc={props.item?.content}
              />
            </div>
          </td>
        );
      }

      return (
        <td>
          <div className="thumbnail-container" />
        </td>
      );
    default: {
      return <td className="cb-cell">{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object,
  intl: intlShape
};

class ContentBuilderList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      lastSearch: ''
    };
  }
  headerCell(property, formatMessage) {
    return formatMessage({
      id: `components.ecs.contentBuilderModal.${property}`
    });
  }

  columns() {
    return {
      disabled_reason: { width: '15px' },
      thumbnail: { width: '66px' },
      name: { sortable: false },
      type: { width: '100px' }
    };
  }

  tableData(items) {
    return (items || []).map(item => ({
      ...item,
      type: item.assetType?.displayName,
      id: item.id.toString(),
      classNameRow: item.disabled ? 'disabled' : 'selectable'
    }));
  }

  renderHeader() {
    return (
      <div className="content-builder-header">
        <div className="content-builder-info">
          <FormattedMessage id="components.ecs.contentBuilderModal.info" />
        </div>
      </div>
    );
  }

  search() {
    this.setState({ lastSearch: this.state.search });
    this.props.actions.searchContentBuilder(this.state.search);
  }

  handleKeyPress(event) {
    if (event.key === 'Enter') {
      this.search();
    }
  }

  clear() {
    this.setState({ search: '' });
    const hasLastSearch = this.state.lastSearch && this.state.lastSearch.length;
    if (hasLastSearch) {
      this.search();
    }
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;
    const selection = items.filter(
      item => item.id.toString() === this.props.selectedContent?.id
    );
    return (
      <DataTable
        items={items}
        fixedLayout
        className="content-builder-table"
        selection={selection}
        selectRows={this.props.rowSelectionMode}
        onRowChange={this.props.onContentSelect}
        noRowHover
        unbufferedCell
      >
        {Object.entries(this.columns()).map((column, index) => {
          const sortable = column[1].sortable;
          const width = column[1].width;
          const property = column[0];
          return (
            <DataTableColumn key={index} sortable={sortable}>
              <CustomDataTableCell
                property={property}
                width={width}
                formatMessage={formatMessage}
                label={this.headerCell(property, formatMessage)}
                title={index}
                intl={this.props.intl}
              />
            </DataTableColumn>
          );
        })}
      </DataTable>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const items = this.tableData(this.props.contentBuilder.data?.assets || []);
    const clearButton = (
      <InputIcon
        assistiveText={{
          icon: 'Clear'
        }}
        name="clear"
        category="utility"
        onClick={() => this.clear()}
      />
    );
    const hasSearch = this.state.search && this.state.search.length > 0;
    return (
      <div className="slds-card-wrapper min-height-x-large card-bg content-builder-card">
        {this.renderHeader()}
        <div className="cb-search">
          <Input
            assistiveText={{
              label: formatMessage({
                id: 'components.ecs.contentBuilderModal.header'
              })
            }}
            placeholder={formatMessage({
              id: 'components.ecs.contentBuilderModal.placeholder'
            })}
            hasSpinner={this.props.contentBuilder.isLoading}
            iconLeft={
              <InputIcon
                assistiveText={{
                  icon: 'Search'
                }}
                name="search"
                category="utility"
              />
            }
            iconRight={hasSearch ? clearButton : null}
            className="cb-search-box"
            value={this.state.search}
            onChange={(evt, { value }) => this.setState({ search: value })}
            onKeyPress={this.handleKeyPress.bind(this)}
          />
        </div>
        <div className="content-builder-table-container">
          {this.renderTable(items)}
        </div>
      </div>
    );
  }
}

ContentBuilderList.propTypes = {
  actions: PropTypes.object,
  contentBuilder: PropTypes.object.isRequired,
  selectedContent: PropTypes.object,
  onContentSelect: PropTypes.func.isRequired,
  rowSelectionMode: PropTypes.string,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ContentBuilderList);
